import { useContext } from "react";
import { useStore } from "zustand";
import { shallow } from "zustand/shallow";
import { EditorStoreContext } from "./EditorStoreContext";

/**
 * Base hook: read from the editor store via context.
 * Every component below <EditorStoreContext.Provider> can use this.
 */
export const useEditorStore = (selector) => {
  const store = useContext(EditorStoreContext);
  if (!store) throw new Error("useEditorStore must be used inside EditorStoreContext.Provider");
  return useStore(store, selector);
};

/**
 * Get the raw store object (for imperative access in effects / event handlers).
 */
export const useEditorStoreApi = () => {
  const store = useContext(EditorStoreContext);
  if (!store) throw new Error("useEditorStoreApi must be used inside EditorStoreContext.Provider");
  return store;
};

// ─── Fine-grained selectors ──────────────────────────────────────────────────

// Core data
export const useCells = () => useEditorStore((s) => s.cells);
export const useSelection = () => useEditorStore((s) => s.selection);
export const useIsInitialized = () => useEditorStore((s) => s.isInitialized);
export const useContainerSize = () => useEditorStore((s) => s.containerSize);

// Layout config
export const useLayoutTemplateKey = () => useEditorStore((s) => s.layoutTemplateKey);
export const useGutterSize = () => useEditorStore((s) => s.gutterSize);
export const usePageSizeKey = () => useEditorStore((s) => s.pageSizeKey);
export const useComicStyleKey = () => useEditorStore((s) => s.comicStyleKey);
export const useStoryboard = () => useEditorStore((s) => s.storyboard);

// Snap config
export const useSnapGridSize = () => useEditorStore((s) => s.snapGridSize);
export const useSnapEnabled = () => useEditorStore((s) => s.snapEnabled);
export const useShowSnapGrid = () => useEditorStore((s) => s.showSnapGrid);

// Export config
export const useExportDpi = () => useEditorStore((s) => s.exportDpi);
export const useExportFormat = () => useEditorStore((s) => s.exportFormat);

// View config
export const useShowGuides = () => useEditorStore((s) => s.showGuides);
export const useShowReadingOrder = () => useEditorStore((s) => s.showReadingOrder);
export const useReadingDirection = () => useEditorStore((s) => s.readingDirection);
export const useDarkMode = () => useEditorStore((s) => s.darkMode);
export const useZoom = () => useEditorStore((s) => s.zoom);
export const useZoomMode = () => useEditorStore((s) => s.zoomMode);
export const useKnifeMode = () => useEditorStore((s) => s.knifeMode);

// UI state
export const useShowAIModal = () => useEditorStore((s) => s.showAIModal);
export const useContextMenu = () => useEditorStore((s) => s.contextMenu);
export const useClipboardBubble = () => useEditorStore((s) => s.clipboardBubble);
export const useMultiSelect = () => useEditorStore((s) => s.multiSelect);

// Undo/redo
export const useCanUndo = () => useEditorStore((s) => s.history.length > 0);
export const useCanRedo = () => useEditorStore((s) => s.future.length > 0);

// ─── Derived selectors ───────────────────────────────────────────────────────

export const useSelectedCell = () =>
  useEditorStore((s) => {
    if (!s.selection?.cellId) return null;
    return s.cells.find((c) => c.id === s.selection.cellId) || null;
  });

export const useSelectedBubble = () =>
  useEditorStore((s) => {
    if (s.selection?.type !== "bubble" || !s.selection.bubbleId) return null;
    const cell = s.cells.find((c) => c.id === s.selection.cellId);
    if (!cell) return null;
    return (cell.items || []).find(
      (i) => i.type === "speechBubble" && i.id === s.selection.bubbleId
    ) || null;
  });

export const useSelectedSFX = () =>
  useEditorStore((s) => {
    if (s.selection?.type !== "sfx" || !s.selection.sfxId) return null;
    const cell = s.cells.find((c) => c.id === s.selection.cellId);
    if (!cell) return null;
    return (cell.items || []).find(
      (i) => i.type === "sfx" && i.id === s.selection.sfxId
    ) || null;
  });

export const useCanUnmerge = () =>
  useEditorStore((s) => {
    if (!s.selection?.cellId) return false;
    const cell = s.cells.find((c) => c.id === s.selection.cellId);
    return Boolean(cell?.mergedFrom?.length);
  });

// ─── Actions selector (stable references — no re-render risk) ────────────────

export const useEditorActions = () =>
  useEditorStore(
    (s) => ({
      setCells: s.setCells,
      applyCellUpdate: s.applyCellUpdate,
      captureSnapshot: s.captureSnapshot,
      undo: s.undo,
      redo: s.redo,
      setSelection: s.setSelection,
      clearSelection: s.clearSelection,
      setMultiSelect: s.setMultiSelect,
      setLayoutTemplateKey: s.setLayoutTemplateKey,
      setGutterSize: s.setGutterSize,
      setPageSizeKey: s.setPageSizeKey,
      setComicStyleKey: s.setComicStyleKey,
      setExportDpi: s.setExportDpi,
      setExportFormat: s.setExportFormat,
      toggleGuides: s.toggleGuides,
      toggleReadingOrder: s.toggleReadingOrder,
      setReadingDirection: s.setReadingDirection,
      toggleDarkMode: s.toggleDarkMode,
      setShowAIModal: s.setShowAIModal,
      setContextMenu: s.setContextMenu,
      setClipboardBubble: s.setClipboardBubble,
      setSnapGridSize: s.setSnapGridSize,
      toggleSnapEnabled: s.toggleSnapEnabled,
      toggleShowSnapGrid: s.toggleShowSnapGrid,
      setInitialized: s.setInitialized,
      setContainerSize: s.setContainerSize,
    }),
    shallow
  );
